import type { ReactNode } from 'react';
import { useState } from 'react';
import NextLink from 'next/link';
import { bezier } from '@savvy-kit/foundations/motion/grading';
import { useMotionReduced } from '@savvy-kit/foundations/motion/use-motion-reduced';
import { cn } from '@savvy-kit/lib/utils';
import { MoreHorizontal } from 'lucide-react';
import { motion } from 'motion/react';

import { useTrackEvent } from '@savvy/deprecated-shared/tracking/use-track-event';

import type {
  ClientPortalAccountMenuState,
  ClientPortalNavId,
  ClientPortalNavItem,
  ClientPortalNavState,
} from './types';
import { ClientPortalMobileMoreSheet } from './client-portal-mobile-more-sheet';
import {
  CLIENT_PORTAL_MOBILE_MORE_ELEMENT_NAME,
  CLIENT_PORTAL_MOBILE_MORE_LABEL,
  CLIENT_PORTAL_MOBILE_PRIMARY_NAV_IDS,
  CLIENT_PORTAL_MOBILE_PRIMARY_NAV_ITEMS,
  CLIENT_PORTAL_NAV_ELEMENT_NAMES,
} from './constants';
import { clientPortalNavAvailability, clientPortalNavHref, formatTaskBadgeCount } from './utils';

type ClientPortalMobileTabBarProps = {
  readonly activeNavId: ClientPortalNavId | null;
  readonly clientName: string;
  readonly clientEmail: string | null;
  readonly clientAvatarUrl: string | null;
  readonly nav: ClientPortalNavState;
  readonly openTaskCount: number;
  readonly accountMenu: ClientPortalAccountMenuState;
};

// Kept off `globals.css`: a global keyframe is a design-system change, and the pop belongs to this one icon.
const ICON_POP = {
  scale: [1, 1.18, 0.95, 1],
  transition: { duration: 0.28, times: [0, 0.4, 0.7, 1], ease: bezier([0.22, 1, 0.36, 1]) },
};

const TAB_CELL_CLASS =
  'flex min-h-14 flex-1 basis-0 cursor-pointer flex-col items-center justify-center gap-1 rounded-md px-1 py-2 text-center outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-inset';

function TabIcon({
  item,
  isActive,
  count,
}: {
  readonly item: ClientPortalNavItem;
  readonly isActive: boolean;
  readonly count: number;
}) {
  const Icon = item.icon;
  const reduced = useMotionReduced();
  return (
    <motion.span
      // Remounted when the tab flips, so the pop replays rather than animating once on the first mount.
      key={isActive ? 'active' : 'inactive'}
      className={cn('relative flex items-center justify-center rounded-full px-3 py-1', isActive && 'bg-primary/10')}
      animate={isActive && !reduced ? { scale: ICON_POP.scale } : undefined}
      transition={ICON_POP.transition}
    >
      <Icon size={22} active={isActive} />
      {count > 0 && (
        <span className="absolute -top-1 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] leading-none font-semibold text-white">
          {formatTaskBadgeCount(count)}
        </span>
      )}
    </motion.span>
  );
}

function TabLabel({ label, isActive }: { readonly label: string; readonly isActive: boolean }) {
  return <span className={cn('text-[10px] leading-none', isActive ? 'font-semibold' : 'font-medium')}>{label}</span>;
}

function tabToneClass(isActive: boolean, isUnavailable: boolean) {
  if (isUnavailable) {
    return 'cursor-default text-secondary/50';
  }
  return isActive ? 'text-primary' : 'text-secondary';
}

export function ClientPortalMobileTabBar({
  activeNavId,
  clientName,
  clientEmail,
  clientAvatarUrl,
  nav,
  openTaskCount,
  accountMenu,
}: ClientPortalMobileTabBarProps) {
  const trackInteraction = useTrackEvent('userInteraction');
  const [moreOpen, setMoreOpen] = useState(false);
  const isMoreActive = activeNavId === null || !CLIENT_PORTAL_MOBILE_PRIMARY_NAV_IDS.includes(activeNavId);

  return (
    <>
      <nav
        aria-label="Portal navigation"
        className="flex shrink-0 items-stretch border-t border-border-light bg-card pb-[env(safe-area-inset-bottom)] md:hidden print:hidden"
      >
        {CLIENT_PORTAL_MOBILE_PRIMARY_NAV_ITEMS.map((item) => {
          const isActive = activeNavId === item.id;
          const href =
            clientPortalNavAvailability(item.id, nav) === 'available'
              ? clientPortalNavHref(item.id, nav.personalPortfolioId)
              : null;
          const count = item.showBadge === true ? openTaskCount : 0;
          const label = count > 0 ? `${item.label}, ${count} ${count === 1 ? 'open task' : 'open tasks'}` : item.label;
          const content: ReactNode = (
            <>
              <TabIcon item={item} isActive={isActive} count={count} />
              <TabLabel label={item.label} isActive={isActive} />
            </>
          );

          if (href === null) {
            return (
              <span
                key={item.id}
                aria-disabled
                aria-label={label}
                className={cn(TAB_CELL_CLASS, tabToneClass(isActive, true))}
              >
                {content}
              </span>
            );
          }

          return (
            <NextLink
              key={item.id}
              href={href}
              aria-label={label}
              aria-current={isActive ? 'page' : undefined}
              className={cn(TAB_CELL_CLASS, tabToneClass(isActive, false))}
              onClick={() => {
                trackInteraction({
                  elementName: CLIENT_PORTAL_NAV_ELEMENT_NAMES[item.id],
                  htmlElement: 'link',
                  interactionType: 'button_click',
                });
              }}
            >
              {content}
            </NextLink>
          );
        })}
        <button
          type="button"
          aria-label={CLIENT_PORTAL_MOBILE_MORE_LABEL}
          aria-current={isMoreActive ? true : undefined}
          aria-haspopup="dialog"
          aria-expanded={moreOpen}
          className={cn(TAB_CELL_CLASS, tabToneClass(isMoreActive, false))}
          onClick={() => {
            trackInteraction({
              elementName: CLIENT_PORTAL_MOBILE_MORE_ELEMENT_NAME,
              htmlElement: 'button',
              interactionType: 'button_click',
            });
            setMoreOpen(true);
          }}
        >
          <span
            className={cn(
              'relative flex items-center justify-center rounded-full px-3 py-1',
              isMoreActive && 'bg-primary/10',
            )}
          >
            <MoreHorizontal
              size={22}
              strokeWidth={isMoreActive ? 2 : 1.5}
              fill={isMoreActive ? 'currentColor' : 'none'}
            />
          </span>
          <TabLabel label={CLIENT_PORTAL_MOBILE_MORE_LABEL} isActive={isMoreActive} />
        </button>
      </nav>
      <ClientPortalMobileMoreSheet
        open={moreOpen}
        onOpenChange={setMoreOpen}
        activeNavId={activeNavId}
        clientName={clientName}
        clientEmail={clientEmail}
        clientAvatarUrl={clientAvatarUrl}
        nav={nav}
        openTaskCount={openTaskCount}
        accountMenu={accountMenu}
      />
    </>
  );
}
